import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import { markMatchesInHtml, useFeedFind } from '../feedFind';

interface EvenniaMessageProps {
  content: string;
  className?: string;
  presentation?: 'terminal' | 'prose';
}

// Evennia color class mappings to Tailwind classes
const colorMap: Record<string, string> = {
  'color-000': 'text-gray-900', // Black
  'color-001': 'text-red-700', // Dark Red
  'color-002': 'text-green-700', // Dark Green
  'color-003': 'text-yellow-600', // Dark Yellow
  'color-004': 'text-blue-700', // Dark Blue
  'color-005': 'text-purple-700', // Dark Magenta
  'color-006': 'text-cyan-700', // Dark Cyan
  'color-007': 'text-gray-400', // Light Gray
  'color-008': 'text-gray-600', // Dark Gray
  'color-009': 'text-red-400', // Light Red
  'color-010': 'text-green-400', // Light Green
  'color-011': 'text-yellow-400', // Light Yellow
  'color-012': 'text-blue-400', // Light Blue
  'color-013': 'text-purple-400', // Light Magenta
  'color-014': 'text-cyan-400', // Light Cyan
  'color-015': 'text-white', // White
};

export function EvenniaMessage({
  content,
  className = '',
  presentation = 'terminal',
}: EvenniaMessageProps) {
  // Find in this session (#4129): matches are marked after sanitizing, so the
  // only `<mark>` in the HTML is ours.
  const find = useFeedFind();
  const processedContent = useMemo(() => {
    // Convert Evennia color classes to Tailwind classes
    let processed = content;

    // Replace color classes
    Object.entries(colorMap).forEach(([evenniaClass, tailwindClass]) => {
      const regex = new RegExp(`class="${evenniaClass}"`, 'g');
      processed = processed.replace(regex, `class="${tailwindClass}"`);
    });

    // Convert line breaks
    processed = processed.replace(/<br>/g, '\n');

    // Messages carry user-authored text (poses, says, names) routed through
    // Evennia's ANSI→HTML conversion, so the string is untrusted. Sanitize
    // before injecting: DOMPurify strips scripts/event-handlers/dangerous tags
    // while keeping the legitimate color <span>s.
    return markMatchesInHtml(DOMPurify.sanitize(processed), find);
  }, [content, find]);

  return (
    <div
      // [overflow-wrap:anywhere] (#3862): the same wrap rule FormattedContent
      // carries, so a system line never widens the feed sideways either.
      className={`whitespace-pre-wrap [overflow-wrap:anywhere] ${presentation === 'terminal' ? 'font-mono text-sm' : 'font-[inherit] text-[length:inherit] leading-[1.55]'} ${className}`}
      dangerouslySetInnerHTML={{ __html: processedContent }}
    />
  );
}
